import { Tooltip } from "@/base";
import {
    Button,
    Chip,
    cn,
    Select as NextUISelect,
    type SelectedItems,
    SelectItem,
} from "@heroui/react";
import { IconBulb, IconInfoCircle } from "@tabler/icons-react";
import Color from "color";
import {
    type ChangeEvent,
    type FocusEvent,
    type ReactNode,
    useCallback,
    useMemo,
    useState,
} from "react";

interface Item {
    id: string | number;
    name: string;
    color?: string;
    type?: string;
    nextUiColor?:
        | "primary"
        | "secondary"
        | "success"
        | "warning"
        | "danger"
        | "default";
}

export interface BaseSelectProps {
    items: Item[];
    label?: string;
    asChips?: boolean;
    isRequired?: boolean;
    asInt?: boolean;
    asBoolean?: boolean;
    isDisabled?: boolean;
    disallowEmptySelection?: boolean;
    className?: string;
    value?: string | number;
    description?: ReactNode;
    hint?: ReactNode;
    placeholder?: string;
    fullHeight?: boolean;
    name?: string;
    isViewOnly?: boolean;
    renderItem?: (item: Item) => ReactNode;
    startContentRender?: (itemId: string | number) => ReactNode;
    renderValue?: (items: SelectedItems<Item>) => ReactNode;
    formatCopyValue?: (value: string | number) => string;
    startContent?: ReactNode;
    selectorIcon?: ReactNode;
    tabIndex?: number;
}

type SelectProps = BaseSelectProps & {
    onChange?: (value: number | string | boolean) => void;
    onBlur?: (e: FocusEvent<Element, Element>) => void;
    errorMessage?: string;
    isInvalid?: boolean;
};

const parseBoolean = (value: string) => {
    if (value === "true") {
        return true;
    }

    if (value === "false") {
        return false;
    }

    return value;
};

const SelectChip = ({ name, color }: Pick<Item, "name" | "color">) => (
    <Chip
        size="sm"
        style={
            color && {
                backgroundColor: new Color(color).lightness(85).toString(),
            }
        }
        classNames={{
            base: "min-w-0 w-full",
            content: "truncate",
        }}
    >
        {name}
    </Chip>
);

export const Select = ({
    items,
    onChange,
    onBlur,
    value,
    label,
    asChips,
    isRequired,
    asInt,
    asBoolean,
    isDisabled,
    isInvalid,
    errorMessage,
    disallowEmptySelection,
    className,
    description,
    hint,
    placeholder,
    fullHeight,
    name,
    isViewOnly,
    renderItem,
    startContent,
    startContentRender,
    renderValue,
    formatCopyValue,
    selectorIcon,
    tabIndex,
}: SelectProps) => {
    const [isCopyTooltipOpen, setIsCopyTooltipOpen] = useState(false);

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLSelectElement>) =>
            onChange(
                asInt === true && event.target.value
                    ? Number.parseInt(event.target.value, 10)
                    : asBoolean
                      ? parseBoolean(event.target.value)
                      : event.target.value,
            ),
        [asInt, asBoolean, onChange],
    );

    const handleCopy = useCallback(async () => {
        try {
            const formattedValue = formatCopyValue
                ? formatCopyValue(value)
                : value;

            await navigator.clipboard.writeText(
                label ? `${label}: ${formattedValue}` : `${formattedValue}`,
            );
            setIsCopyTooltipOpen(true);
            setTimeout(() => setIsCopyTooltipOpen(false), 1000);
        } catch (err) {
            console.error("Failed to copy text: ", err);
        }
    }, [value, label, formatCopyValue]);

    const selectedKeys = useMemo(
        () =>
            value !== null && value !== undefined
                ? // Need to convert to string because NextUI expects string keys
                  [value.toString()]
                : [],
        [value],
    );

    const selectedItem = useMemo(
        () =>
            value !== null && value !== undefined
                ? items.find((item) => item.id === value.toString())
                : undefined,
        [value, items],
    );

    const finalRenderValue = useMemo(() => {
        if (renderValue) {
            return renderValue;
        }

        if (asChips) {
            return (items) =>
                items.map((item) => (
                    <SelectChip
                        key={item.key}
                        name={item.data.name}
                        color={item.data.color}
                    />
                ));
        }

        return undefined;
    }, [asChips, renderValue]);

    if (!items) {
        return null;
    }

    return (
        <div className={cn("relative w-full", className)}>
            <NextUISelect
                aria-label={label}
                label={label}
                name={name}
                disallowEmptySelection={disallowEmptySelection}
                onChange={handleChange}
                onBlur={onBlur}
                fullWidth={true}
                isRequired={isRequired}
                isDisabled={isDisabled || isViewOnly}
                selectedKeys={selectedKeys}
                items={items || []}
                isInvalid={isInvalid}
                errorMessage={errorMessage}
                renderValue={finalRenderValue}
                color={selectedItem?.nextUiColor}
                listboxProps={{ emptyContent: "Keine Einträge vorhanden" }}
                placeholder={
                    placeholder || (label ? undefined : "Auswählen...")
                }
                className={cn(isViewOnly && "opacity-70")}
                classNames={{
                    mainWrapper: fullHeight ? "h-full" : "",
                    trigger: fullHeight ? "h-full" : "",
                    selectorIcon:
                        description || hint
                            ? description && hint
                                ? "mr-14"
                                : "mr-9"
                            : undefined,
                }}
                startContent={startContent}
                selectorIcon={selectorIcon}
                endContent={
                    <div className="-translate-y-[50%] absolute top-[50%] right-3 flex items-center gap-1">
                        {description && !isViewOnly && (
                            <Tooltip color="primary" content={description}>
                                <Button
                                    variant="light"
                                    isIconOnly={true}
                                    size="sm"
                                    tabIndex={tabIndex}
                                    as="div"
                                >
                                    <IconInfoCircle stroke={1.5} size={18} />
                                </Button>
                            </Tooltip>
                        )}
                        {hint && !isViewOnly && (
                            <Tooltip color="warning" content={hint}>
                                <Button
                                    variant="light"
                                    isIconOnly={true}
                                    size="sm"
                                    tabIndex={tabIndex}
                                    as="div"
                                >
                                    <IconBulb stroke={1.5} size={18} />
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                }
                tabIndex={tabIndex}
            >
                {(item) => (
                    <SelectItem
                        variant="flat"
                        key={item.id}
                        textValue={item.name}
                        color={item.nextUiColor}
                        startContent={startContentRender?.(item.id)}
                    >
                        {asChips ? (
                            <SelectChip name={item.name} color={item.color} />
                        ) : renderItem ? (
                            renderItem(item)
                        ) : (
                            item.name
                        )}
                    </SelectItem>
                )}
            </NextUISelect>
            {!!value && isViewOnly && (
                <Tooltip
                    content="Kopiert"
                    placement="top-end"
                    closeDelay={2000}
                    isOpen={isCopyTooltipOpen}
                >
                    <button
                        type="button"
                        className="absolute top-0 left-0 h-full w-full"
                        onClick={handleCopy}
                    />
                </Tooltip>
            )}
        </div>
    );
};
